import { KeyboardDoubleArrowLeft } from '@mui/icons-material';
import { Box, Collapse, Drawer, IconButton, Stack, Tab, Tabs, Toolbar, Typography } from '@mui/material';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useResize from '../../utils/hooks/useResize';
import { LAYOUT_CONSTANTS } from '../../utils/layout-constants';

const KEYBOARD_RESIZE_STEP_PX = 10;

export interface LeftDrawerTab {
  id: string;
  label: string;
  icon: JSX.Element;
  value: string;
  dividerAbove?: boolean;
  anchorBottom?: boolean;
  stickyHeader?: boolean;
  headerContent?: React.ReactNode;
}

interface LeftDrawerProps {
  drawerWidth: number;
  isOpen: boolean;
  tabs: LeftDrawerTab[];
  selectedTab: string | null;
  onTabClick: (value: string) => void;
  onClose: () => void;
  // Given the current selected tab value, return header { icon, label }
  getHeader: (value: string | null) => { icon: JSX.Element | null; label: string };
  // Render content for the current selected tab value
  renderContent: (value: string | null) => React.ReactNode;
  // Optional per-page resize limits (content width, not including rail)
  minWidth?: number;
  maxWidth?: number;
  // Notify parent when content width changes (used to persist width)
  onWidthChange?: (width: number) => void;
  // When this value changes to a non-null value, reset the content scroll position.
  contentScrollResetKey?: string | null;
  contentRegionId?: string;
  contentRegionLabel?: string;
  contentRegionDescriptionId?: string;
  contentRegionKeyShortcuts?: string;
}

const LeftDrawer: React.FC<LeftDrawerProps> = ({
  drawerWidth,
  isOpen,
  tabs,
  selectedTab,
  onTabClick,
  onClose,
  getHeader,
  renderContent,
  minWidth,
  maxWidth,
  onWidthChange,
  contentScrollResetKey,
  contentRegionId,
  contentRegionLabel,
  contentRegionDescriptionId,
  contentRegionKeyShortcuts,
}) => {
  const { t } = useTranslation();
  const header = getHeader(selectedTab);
  const isContentOpen = isOpen && selectedTab !== null;
  const selectedTabConfig = tabs.find((tab) => tab.value === selectedTab);
  const stickyHeader = Boolean(selectedTabConfig?.stickyHeader);
  const headerContent = selectedTabConfig?.headerContent;
  const MAX_LEFT_WIDTH_PX = 900;
  const RAIL_WIDTH = LAYOUT_CONSTANTS.LEFT_DRAWER_TAB_RAIL_WIDTH;
  const effectiveMinWidth = minWidth ?? 260;
  const effectiveMaxWidth = maxWidth ?? MAX_LEFT_WIDTH_PX;
  const [isHandleHover, setIsHandleHover] = useState(false);
  const {
    size: resizedWidth,
    isResizing,
    enableResize,
    resizeBy,
    resizeTo,
  } = useResize({
    initial: drawerWidth,
    min: effectiveMinWidth,
    max: effectiveMaxWidth,
    axis: 'x',
    edge: 'right',
  });
  const lastReportedWidthRef = useRef<number | null>(null);
  const contentScrollRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    if (contentScrollResetKey == null) return;

    const contentScroll = contentScrollRef.current;
    if (!contentScroll) return;

    contentScroll.scrollTop = 0;
    contentScroll.scrollLeft = 0;
  }, [contentScrollResetKey]);

  // Nudge responsive components (e.g., Monaco) to layout for pointer and keyboard resizing.
  useEffect(() => {
    window.dispatchEvent(new Event('resize'));
  }, [resizedWidth]);

  const handleResizeKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        resizeBy(-KEYBOARD_RESIZE_STEP_PX);
        break;
      case 'ArrowRight':
        event.preventDefault();
        resizeBy(KEYBOARD_RESIZE_STEP_PX);
        break;
      case 'Home':
        event.preventDefault();
        resizeTo(effectiveMinWidth);
        break;
      case 'End':
        event.preventDefault();
        resizeTo(effectiveMaxWidth);
        break;
      default:
        break;
    }
  };

  // Expose live drawer width to CSS so fixed elements (e.g., BottomControlBar) can align instantly
  useEffect(() => {
    const totalWidth = isContentOpen ? resizedWidth + RAIL_WIDTH : RAIL_WIDTH;
    document.documentElement.style.setProperty('--left-drawer-offset', `${totalWidth}px`);
    return () => {
      // Keep last known value; no-op on unmount
    };
  }, [isContentOpen, resizedWidth, RAIL_WIDTH]);

  // Disable bottom bar transition during live resizing to prevent lag
  useEffect(() => {
    const defaultTransition = 'left 0.225s, right 0.225s';
    document.documentElement.style.setProperty('--bottom-bar-transition', isResizing ? 'none' : defaultTransition);
    // no cleanup needed; we leave the last set value
  }, [isResizing]);

  // Notify parent of width changes when not actively resizing; avoid redundant updates
  useEffect(() => {
    if (onWidthChange) {
      if (!isResizing && resizedWidth !== lastReportedWidthRef.current) {
        lastReportedWidthRef.current = resizedWidth;
        onWidthChange(resizedWidth);
      }
    }
  }, [isResizing, resizedWidth, onWidthChange]);

  return (
    <Drawer
      variant="permanent"
      open={isContentOpen}
      sx={{
        overflowX: 'hidden',
        width: isContentOpen ? resizedWidth + RAIL_WIDTH : RAIL_WIDTH,
        flexShrink: 0,
        transition: isResizing
          ? 'none'
          : (theme) =>
              theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
              }),
        willChange: isResizing ? 'width' : undefined,
        [`& .MuiDrawer-paper`]: {
          position: 'relative',
          width: isContentOpen ? resizedWidth + RAIL_WIDTH : RAIL_WIDTH,
          boxSizing: 'border-box',
          overflowY: 'visible',
          willChange: 'width',
          transition: isResizing ? 'none' : undefined,
          contain: 'layout style',
        },
      }}
    >
      <Toolbar />
      <Stack direction="row" height="100%" sx={{ overflowX: 'hidden', overflowY: 'visible' }}>
        {/* Vertical Tabs rail */}
        <Box sx={{ width: '100%', minWidth: `${RAIL_WIDTH}px`, maxWidth: `${RAIL_WIDTH}px` }}>
          <Tabs
            orientation="vertical"
            value={selectedTab ?? false}
            indicatorColor="secondary"
            textColor="inherit"
            sx={(theme) => ({
              height: '100%',
              width: '100%',
              '& .MuiTabs-flexContainer': {
                height: '100%',
              },
              '& .MuiTabs-indicator': {
                backgroundColor: theme.vars?.palette?.accent?.main,
                width: 4,
              },
              '& .MuiTab-root': {
                fontSize: '0.75rem',
                minWidth: '94px',
                color: `${theme.vars?.palette?.text?.primary}`,
                opacity: 1,
                '&.Mui-selected': {
                  backgroundColor: theme.vars?.palette?.accent?.opaque,
                },
              },
            })}
          >
            {tabs.map((tab) => (
              <Tab
                key={tab.id}
                label={tab.label}
                icon={tab.icon}
                value={tab.value}
                onClick={() => onTabClick(tab.value)}
                aria-label={tab.label}
                sx={{
                  ...(tab.anchorBottom
                    ? {
                        mt: 'auto',
                      }
                    : {}),
                  ...(tab.dividerAbove
                    ? {
                        borderTop: 1,
                        borderColor: 'divider',
                        mt: tab.anchorBottom ? 'auto' : 1,
                        pt: 1,
                      }
                    : {}),
                }}
              />
            ))}
          </Tabs>
        </Box>

        {/* Content Area */}
        <Collapse in={isContentOpen} orientation="horizontal" sx={{ flexShrink: 0, transition: isResizing ? 'none' : undefined }}>
          <Box
            id={contentRegionId}
            role={contentRegionLabel ? 'region' : undefined}
            aria-label={contentRegionLabel}
            aria-describedby={contentRegionDescriptionId}
            aria-keyshortcuts={contentRegionKeyShortcuts}
            ref={stickyHeader ? undefined : contentScrollRef}
            data-testid={stickyHeader ? 'left-drawer-content-frame' : 'left-drawer-content-scroll'}
            sx={{
              display: stickyHeader ? 'flex' : 'block',
              flexDirection: stickyHeader ? 'column' : undefined,
              overflowY: stickyHeader ? 'hidden' : 'auto',
              overflowX: 'hidden',
              height: '100%',
              minHeight: 0,
              borderLeft: 2,
              boxSizing: 'border-box',
              position: 'relative',
            }}
            flexShrink={0}
            // Leave a small gap between the scrollbar and the right edge/resize handle
            width={`calc(${resizedWidth}px - 4px)`}
          >
            {/* Header */}
            {header && (
              <Box
                data-testid="left-drawer-header"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                gap={headerContent ? 1.25 : 0}
                padding={1.75}
                sx={{
                  flexShrink: 0,
                  width: '100%',
                  position: 'relative',
                  zIndex: stickyHeader ? 2 : undefined,
                  backgroundColor: stickyHeader ? 'background.paper' : undefined,
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '95%',
                    height: '2px',
                    backgroundColor: 'accent.main',
                  },
                }}
              >
                <Box display="flex" alignItems="center" justifyContent="center" width="100%" position="relative">
                  <Box display="flex" alignItems="center" gap={1}>
                    {header.icon}
                    <Typography variant="sectionTitle">{header.label}</Typography>
                  </Box>
                  <IconButton aria-label={t('leftDrawer.close.ariaLabel', 'Close drawer')} onClick={onClose} sx={{ position: 'absolute', right: 0 }}>
                    <KeyboardDoubleArrowLeft />
                  </IconButton>
                </Box>
                {headerContent && <Box width="100%">{headerContent}</Box>}
              </Box>
            )}

            <Box
              ref={stickyHeader ? contentScrollRef : undefined}
              data-testid={stickyHeader ? 'left-drawer-content-scroll' : undefined}
              sx={{
                flexGrow: 1,
                minHeight: stickyHeader ? 0 : undefined,
                overflowY: stickyHeader ? 'auto' : 'visible',
                overflowX: 'hidden',
                pt: stickyHeader ? 1 : 0,
                width: '100%',
              }}
            >
              {renderContent(selectedTab)}
            </Box>
          </Box>
        </Collapse>
      </Stack>
      {isContentOpen && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            width: '6px',
            cursor: 'col-resize',
            zIndex: 3,
          }}
          role="separator"
          tabIndex={0}
          aria-orientation="vertical"
          aria-label={t('leftDrawer.resize.ariaLabel', 'Resize left drawer')}
          aria-valuemin={effectiveMinWidth}
          aria-valuemax={effectiveMaxWidth}
          aria-valuenow={resizedWidth}
          aria-valuetext={t('leftDrawer.resize.valueText', '{{width}} pixels', { width: resizedWidth })}
          data-resize-handle="left-drawer"
          onMouseEnter={() => setIsHandleHover(true)}
          onMouseLeave={() => setIsHandleHover(false)}
          onMouseDown={(e) => enableResize(e as unknown as MouseEvent)}
          onKeyDown={handleResizeKeyDown}
        />
      )}
      {/* Highlight line during resize */}
      {(isResizing || isHandleHover) && (
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            width: '2px',
            bgcolor: 'text.primary',
            pointerEvents: 'none',
            zIndex: 4,
          }}
        />
      )}
    </Drawer>
  );
};

export default LeftDrawer;
